import { Link } from "react-router-dom";
import { T } from "@/lib/theme";
import EMark from "@/components/EMark";

/* ============================================================
 * Icon variants — each renders at 100×100 viewBox.
 * They use the established palette:
 *   warm   = #E6A55A (Erenown accent)
 *   cream  = #E8E1D2 (BrandReveal cream)
 *   accent = #A9C8D8 (cool/orbit)
 * ============================================================ */

/**
 * Icon A — Monumental E
 * Pure Bricolage Grotesque “E”, the same letter-form already established
 * by the opening BrandReveal animation. Strongest direct continuity:
 * whoever sees the icon recognises the homepage’s signature moment.
 */
export function IconMonumentalE({ tone = "warm" }) {
  const fill = tone === "warm" ? T.warm : T.text;
  return (
    <svg viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg">
      <text
        x="50" y="58"
        textAnchor="middle"
        dominantBaseline="middle"
        fontFamily="'Bricolage Grotesque', system-ui, sans-serif"
        fontWeight="800"
        fontSize="96"
        letterSpacing="-6"
        fill={fill}
        style={{ textTransform: "uppercase" }}
      >
        E
      </text>
    </svg>
  );
}

/**
 * Icon B — The Anchor E  (PRIMARY RECOMMENDATION)
 * The Monumental E with the middle arm subtly extending past the letter
 * form, ending in a small warm dot. Reads on three levels:
 *   1. The E for Erenown.
 *   2. A central structure with one line projected outward.
 *   3. An anchor reaching beyond itself — the orbit emanating from centre.
 */
export function IconAnchorE() {
  return (
    <svg viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg">
      <text
        x="50" y="58"
        textAnchor="middle"
        dominantBaseline="middle"
        fontFamily="'Bricolage Grotesque', system-ui, sans-serif"
        fontWeight="800"
        fontSize="96"
        letterSpacing="-6"
        fill={T.text}
        style={{ textTransform: "uppercase" }}
      >
        E
      </text>
      {/* projected line out of the middle arm of the E */}
      <line x1="76" y1="50" x2="98" y2="50" stroke={T.warm} strokeWidth="3" strokeLinecap="round" />
      {/* warm anchor dot */}
      <circle cx="98" cy="50" r="3" fill={T.warm} />
    </svg>
  );
}

/**
 * Icon C — The Center Mark
 * A wordless geometric mark: cool circle (the orbit), a faint dashed
 * outer ring, a thin hairline cross, and a single warm dot at centre
 * (Erenown). Works at 16×16 favicon size where letterforms cannot.
 */
export function IconCenterMark() {
  return (
    <svg viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg">
      <circle cx="50" cy="50" r="38" fill="none" stroke={T.accent} strokeWidth="1.6" />
      <circle cx="50" cy="50" r="46" fill="none" stroke={T.borderSoft} strokeWidth="1" strokeDasharray="2 4" />
      <line x1="50" y1="2"  x2="50" y2="14" stroke={T.borderSoft} strokeWidth="1" />
      <line x1="50" y1="86" x2="50" y2="98" stroke={T.borderSoft} strokeWidth="1" />
      <line x1="2"  y1="50" x2="14" y2="50" stroke={T.borderSoft} strokeWidth="1" />
      <line x1="86" y1="50" x2="98" y2="50" stroke={T.borderSoft} strokeWidth="1" />
      <circle cx="50" cy="50" r="6.5" fill={T.warm} />
    </svg>
  );
}

/**
 * Icon D — The Sealed E
 * A compact disc derived from the Orbit Register seal: concentric warm
 * rings (engraved hairline + solid inner), the Bricolage E in the warm
 * core. Reads formal, archival, certificate-grade — for letterheads,
 * signatures, and document marks more than UI.
 */
export function IconSealedE() {
  return (
    <svg viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg">
      <circle cx="50" cy="50" r="46" fill="none" stroke={T.warm} strokeOpacity="0.6" strokeWidth="1" strokeDasharray="2 4" />
      <circle cx="50" cy="50" r="40" fill="none" stroke={T.warm} strokeOpacity="0.85" strokeWidth="1.2" />
      <circle cx="50" cy="50" r="32" fill={T.bg} />
      <circle cx="50" cy="50" r="32" fill="none" stroke={T.warm} strokeOpacity="0.5" strokeWidth="0.8" />
      <text
        x="50" y="56"
        textAnchor="middle"
        dominantBaseline="middle"
        fontFamily="'Bricolage Grotesque', system-ui, sans-serif"
        fontWeight="800"
        fontSize="44"
        letterSpacing="-2"
        fill={T.warm}
        style={{ textTransform: "uppercase" }}
      >
        E
      </text>
    </svg>
  );
}

/* ============================================================
 * Wordmark lockup — icon + “renown” word at the right
 * ============================================================ */
function Wordmark({ icon: Icon, sizePx = 36, gap = 12 }) {
  return (
    <div className="inline-flex items-center" style={{ gap }}>
      <div style={{ width: sizePx, height: sizePx, lineHeight: 0 }}>
        <Icon />
      </div>
      <span
        className="font-bricolage font-semibold uppercase"
        style={{
          color: T.text,
          letterSpacing: "0.22em",
          fontSize: sizePx * 0.42,
        }}
      >
        renown
      </span>
    </div>
  );
}

/* ============================================================
 * Display row: shows an icon at three sizes + wordmark lockup
 * ============================================================ */
function Row({ id, label, code, recommended, icon: Icon, rationale }) {
  return (
    <div
      data-testid={`icon-row-${id}`}
      className="grid grid-cols-12 gap-8 md:gap-12 items-center border-t py-10 md:py-14"
      style={{ borderColor: T.border }}
    >
      {/* Label + rationale */}
      <div className="col-span-12 md:col-span-4">
        <div className="flex items-center gap-3 font-plexmono text-[10px] tracking-[0.3em] uppercase" style={{ color: T.muted }}>
          <span style={{ color: T.warm }}>{code}</span>
          <span className="h-px w-8" style={{ background: T.border }} />
          {recommended && (
            <span
              className="px-2 py-0.5 border"
              style={{
                borderColor: `${T.warm}66`,
                color: T.warm,
              }}
            >
              Recommended
            </span>
          )}
        </div>
        <h3
          className="font-fraunces font-light text-3xl md:text-4xl mt-4 leading-[1.1]"
          style={{ color: T.text, fontVariationSettings: "'SOFT' 50" }}
        >
          {label}
        </h3>
        <p className="mt-5 max-w-sm font-intertight text-[14.5px] leading-[1.85]" style={{ color: T.textSoft }}>
          {rationale}
        </p>
      </div>

      {/* Icon at three sizes — dark ground */}
      <div className="col-span-12 md:col-span-5">
        <div
          className="grid grid-cols-3 gap-px"
          style={{ background: T.border }}
        >
          {[96, 48, 24].map((s) => (
            <div
              key={s}
              className="flex items-center justify-center"
              style={{ background: T.bg, padding: 24, minHeight: 130 }}
            >
              <div style={{ width: s, height: s, lineHeight: 0 }}>
                <Icon />
              </div>
            </div>
          ))}
        </div>
        <div
          className="mt-2 grid grid-cols-3 gap-px font-plexmono text-[9.5px] tracking-[0.3em] uppercase"
          style={{ color: T.muted }}
        >
          <div className="text-center">96 px</div>
          <div className="text-center">48 px</div>
          <div className="text-center">24 px · favicon</div>
        </div>
      </div>

      {/* Wordmark lockup */}
      <div className="col-span-12 md:col-span-3">
        <div
          className="border p-6 flex flex-col gap-6 items-start"
          style={{ borderColor: T.border, background: T.surface }}
        >
          <div
            className="font-plexmono text-[9.5px] tracking-[0.3em] uppercase"
            style={{ color: T.muted }}
          >
            Wordmark Lockup
          </div>
          <Wordmark icon={Icon} sizePx={48} />
          <Wordmark icon={Icon} sizePx={28} />
        </div>
      </div>
    </div>
  );
}

export default function Icons() {
  const variants = [
    {
      id: "anchor-e",
      code: "ICON · B",
      label: "The Anchor E",
      recommended: true,
      icon: IconAnchorE,
      rationale:
        "The Bricolage E with the middle arm projected outward to a small warm dot. Reads as the E for Erenown, a central structure with one line reaching, and an anchor that radiates. Strongest brand continuity with the opening reveal — without becoming a generic letter.",
    },
    {
      id: "monumental-e",
      code: "ICON · A",
      label: "The Monumental E",
      icon: IconMonumentalE,
      rationale:
        "Pure Bricolage Grotesque E, identical to the letterform at the heart of the homepage opening. Direct, monumental, unmistakably tied to the website. Best when paired with the wordmark; less distinctive at favicon scale where many brands also use a single E.",
    },
    {
      id: "sealed-e",
      code: "ICON · D",
      label: "The Sealed E",
      icon: IconSealedE,
      rationale:
        "A compact disc derived from the Orbit Register seal — engraved warm rings around a Bricolage E. Reads formal and certificate-grade. Best for letterheads, signatures, document marks, and corporate stationery. A touch decorative for a 24 px favicon, so usually paired with Icon B in UI.",
    },
    {
      id: "center-mark",
      code: "ICON · C",
      label: "The Center Mark",
      icon: IconCenterMark,
      rationale:
        "Wordless geometric mark — a cool orbit ring, a faint dashed outer ring, a hairline cardinal cross, and one warm dot at centre. Holds up at 16 × 16 px where any letterform breaks. Pairs well alongside Icon B as a secondary mark for app tiles, watermarks, and favicons.",
    },
  ];

  return (
    <div data-testid="icons-page" style={{ background: T.bg, color: T.text }}>
      {/* HERO */}
      <section className="relative overflow-hidden" style={{ background: T.bg }}>
        <div
          aria-hidden
          className="absolute inset-0 opacity-25 pointer-events-none"
          style={{
            backgroundImage: `radial-gradient(circle at center, ${T.border}66 1px, transparent 1px)`,
            backgroundSize: "48px 48px",
            maskImage: "radial-gradient(ellipse 70% 60% at 50% 30%, black, transparent 82%)",
          }}
        />
        <div className="relative max-w-[1300px] mx-auto px-6 md:px-12 pt-16 md:pt-24 pb-16">
          <div
            className="flex items-center gap-4 font-plexmono text-[10.5px] tracking-[0.28em] uppercase"
            style={{ color: T.muted }}
          >
            <span className="inline-flex items-center gap-2"><EMark /> · ICON · MMXX</span>
            <span className="flex-1 h-px" style={{ background: T.border }} />
            <span style={{ color: T.accent }}>04 directions · review</span>
          </div>

          <h1
            className="font-fraunces font-light text-5xl sm:text-6xl lg:text-[68px] leading-[1.04] tracking-[-0.015em] mt-10 max-w-3xl"
            style={{ color: T.text, fontVariationSettings: "'SOFT' 50" }}
          >
            Icon proposal for <span className="italic" style={{ color: T.accent }}>Erenown</span>.
          </h1>
          <p className="mt-8 max-w-2xl font-intertight text-[16px] leading-[1.9]" style={{ color: T.textSoft }}>
            Four directions, drawn directly from the visual system already in place: the monumental E from the opening reveal, the warm anchor of the orbit, and the engraved seal of the register. My recommendation is <strong style={{ color: T.warm }}>The Anchor E</strong> — strongest brand continuity, distinct at every size, and quietly charged with the “central structure” idea.
          </p>
        </div>
      </section>

      {/* ROWS */}
      <section className="border-t" style={{ borderColor: T.border, background: T.surface }}>
        <div className="max-w-[1300px] mx-auto px-6 md:px-12 py-10 md:py-16">
          {variants.map((v) => (
            <Row key={v.id} {...v} />
          ))}
        </div>
      </section>

      {/* FOOTER STRIP */}
      <section className="border-t" style={{ borderColor: T.border }}>
        <div className="max-w-[1300px] mx-auto px-6 md:px-12 py-12 flex flex-wrap items-end justify-between gap-6">
          <p className="max-w-xl font-intertight text-[14.5px] leading-[1.85]" style={{ color: T.textSoft }}>
            Once you pick a direction, I’ll wire it into the site as the header wordmark icon, the favicon, and the document marks — replacing the current text-only treatment.
          </p>
          <Link
            to="/contact"
            className="group inline-flex items-center gap-3 px-7 py-4 font-plexmono text-[11px] tracking-[0.3em] uppercase border transition-colors"
            style={{ borderColor: `${T.warm}66`, color: T.warm }}
          >
            Discuss the direction
          </Link>
        </div>
      </section>
    </div>
  );
}
